import React, { useCallback } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import InputIcon from '@mui/icons-material/Input';
import { ShapeInput, SliderControl, SwitchControl } from '../../core/NodeControls';

/**
 * InputNode represents the entry point of data into the neural network
 */
const InputNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
          Data Input
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: '100%',
            height: '30px',
            background: alpha(theme.palette.info.main, 0.1),
            borderRadius: '4px',
            border: `1px dashed ${alpha(theme.palette.info.main, 0.5)}`,
          }}
        >
          <Box
            sx={{
              width: '10px',
              height: '100%',
              backgroundColor: alpha(theme.palette.info.main, 0.3),
              mr: 0.5,
            }}
          />
          <Box
            sx={{
              width: '10px',
              height: '100%',
              backgroundColor: alpha(theme.palette.info.main, 0.5),
              mr: 0.5,
            }}
          />
          <Box
            sx={{
              width: '10px',
              height: '100%',
              backgroundColor: alpha(theme.palette.info.main, 0.7),
            }}
          />
        </Box>
      </>
    );
  };
  
  // Controls for the node configuration popover
  const renderControls = () => {
    return (
      <>
        <SliderControl
          value={data.units || 10}
          onChange={(value) => handleDataChange('units', value)}
          min={1}
          max={1000}
          step={1}
          label="Input Units"
          tooltip="Number of input features"
          marks
        />
        
        <ShapeInput
          value={data.shape || [data.units || 10]}
          onChange={(value) => handleDataChange('shape', value)}
          label="Input Shape"
          tooltip="The shape of the input tensor (excluding batch dimension)"
        />
        
        <SliderControl
          value={data.batch_size || 32}
          onChange={(value) => handleDataChange('batch_size', value)}
          min={1}
          max={512}
          step={1}
          label="Batch Size"
          tooltip="Number of samples per gradient update (null = dynamic)"
        />
        
        <SwitchControl
          checked={data.stateful || false}
          onChange={(value) => handleDataChange('stateful', value)}
          label="Stateful"
          tooltip="If true, the last state for each sample at index i in a batch will be used as initial state for the sample of index i in the following batch."
        />
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<InputIcon fontSize="small" />}
      nodeType="input"
      preview={renderPreview()}
      controls={renderControls()}
      multipleOutputs={false}
      disableInputHandle={true}
      multipleInputs={false}
      style={{
        borderStyle: 'dashed',
      }}
      onDelete={data.onDelete}
    />
  );
};

export default memo(InputNode); 